import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, XCircle, Clock, Share2, Copy, ExternalLink, Loader2, Receipt } from "lucide-react";
import { getTxDetail, getPrices } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useFeeCurrency, formatFee, FeeToggle } from "@/hooks/useFeeCurrency";
import { FADE_UP } from "@/lib/chartStyles";

const KIND = { buy: "Buy", sell: "Sell", swap: "Swap" };
const num = (v, d = 6) => Number(v).toLocaleString(undefined, { maximumFractionDigits: d });
const amt = (a) => `${num(a.amount, a.token === "FMI" ? 6 : 4)} ${a.token}`;
const short = (h) => `${h.slice(0, 10)}…${h.slice(-8)}`;

const Status = ({ tx }) => {
  if (tx.pending) return <span data-testid="receipt-status" className="flex items-center gap-1.5 text-yellow-400"><Clock size={15} /> Pending</span>;
  return tx.status === 1
    ? <span data-testid="receipt-status" className="flex items-center gap-1.5 text-green-400"><CheckCircle2 size={15} /> Successful</span>
    : <span data-testid="receipt-status" className="flex items-center gap-1.5 text-red-400"><XCircle size={15} /> Failed</span>;
};

const Row = ({ label, children, testId }) => (
  <div className="flex items-center justify-between gap-4 py-2.5 border-b border-[#2A2411] last:border-0 text-sm">
    <span className="text-zinc-500">{label}</span>
    <span data-testid={testId} className="font-mono text-white text-right break-all">{children}</span>
  </div>
);

const ShareBar = ({ isTrade }) => {
  const url = window.location.href;
  const text = isTrade ? "My trade on FMI Smart Chain" : "Transaction on FMI Smart Chain";
  const copy = () => { navigator.clipboard.writeText(url); toast.success("Receipt link copied"); };
  const share = () => navigator.share?.({ title: text, url }).catch(() => {});
  return (
    <div className="flex flex-wrap gap-2">
      <Button data-testid="receipt-copy-link-btn" onClick={copy} variant="outline" className="border-[#2A2411] text-zinc-300"><Copy size={14} className="mr-1.5" /> Copy link</Button>
      {navigator.share && <Button data-testid="receipt-share-btn" onClick={share} variant="outline" className="border-[#2A2411] text-zinc-300"><Share2 size={14} className="mr-1.5" /> Share</Button>}
      <a data-testid="receipt-share-x" target="_blank" rel="noreferrer" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`}
        className="inline-flex items-center h-9 px-3 rounded-md border border-[#2A2411] text-sm text-zinc-300 hover:text-white transition-colors">Post on X</a>
    </div>
  );
};

const TradeDetails = ({ tx, usd }) => {
  const t = tx.trade;
  const { currency, toggle } = useFeeCurrency();
  const rate = t.paid.amount ? t.received.amount / t.paid.amount : 0;
  return (
    <>
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 sm:gap-6 py-6">
        <div><p className="text-xs text-zinc-500">You paid</p><p data-testid="receipt-paid" className="font-display text-2xl sm:text-3xl font-bold text-white">{amt(t.paid)}</p></div>
        <ArrowRight className="text-[#D4AF37] rotate-90 sm:rotate-0" />
        <div><p className="text-xs text-zinc-500">You received</p><p data-testid="receipt-received" className="font-display text-2xl sm:text-3xl font-bold text-[#D4AF37]">{amt(t.received)}</p></div>
      </div>
      <div className="flex justify-end mb-1"><FeeToggle currency={currency} onToggle={toggle} testId="receipt-fee-toggle" /></div>
      <Row label="Rate" testId="receipt-rate">1 {t.paid.token} = {num(rate, 6)} {t.received.token}</Row>
      <Row label={t.kind === "swap" ? "Pool fee (0.2%)" : "Trading fee (0.15%)"} testId="receipt-fee">{amt(t.fee)}</Row>
      {t.tax && <Row label="Transaction tax" testId="receipt-tax">{amt(t.tax)}</Row>}
      <Row label="Network fee (gas)" testId="receipt-gas">{formatFee(tx.gasFeeFmi, usd, currency)} <span className="text-zinc-500">· {num(tx.gasUsed, 0)} gas</span></Row>
    </>
  );
};

export default function ReceiptPage() {
  const { hash } = useParams();
  const { data: tx, isLoading, isError } = useQuery({ queryKey: ["receipt", hash], queryFn: () => getTxDetail(hash),
    refetchInterval: (q) => (q.state.data?.pending ? 3000 : false) });
  const { data: prices } = useQuery({ queryKey: ["exchangePrices"], queryFn: getPrices, staleTime: 60000 });

  if (isLoading) return <div className="py-24 grid place-items-center"><Loader2 className="animate-spin text-[#D4AF37]" /></div>;
  if (isError || !tx) return <p data-testid="receipt-not-found" className="max-w-2xl mx-auto px-4 py-24 text-zinc-400">Transaction not found.</p>;
  const when = tx.blockTimestamp ? new Date(tx.blockTimestamp * 1000).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <div data-testid="receipt-page" className="max-w-2xl mx-auto px-4 sm:px-6 py-12">
      <motion.div {...FADE_UP}>
        <Card className="border-[#D4AF37]/30 bg-[#0A0A0A] gold-glow">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Receipt size={18} className="text-[#D4AF37]" />
                <h1 className="font-display text-xl font-bold text-white">{tx.trade ? `${KIND[tx.trade.kind]} receipt` : "Transaction receipt"}</h1>
              </div>
              <Status tx={tx} />
            </div>
            <p className="text-xs text-zinc-500 mt-1">FMI Smart Chain · {when}</p>
            {tx.trade ? <TradeDetails tx={tx} usd={prices?.FMI?.usd} /> : (
              <>
                <p data-testid="receipt-not-trade" className="py-6 text-sm text-zinc-400">This transaction isn't a buy, sell or swap. See the full details in the explorer.</p>
                <Row label="Network fee (gas)" testId="receipt-gas">{formatFee(tx.gasFeeFmi, prices?.FMI?.usd, "FMI")}</Row>
              </>
            )}
            <Row label={tx.trade ? "Trader" : "From"} testId="receipt-trader"><Link to={`/address/${tx.trade?.trader || tx.from}`} className="table-link">{short(tx.trade?.trader || tx.from)}</Link></Row>
            <Row label="Block" testId="receipt-block">{tx.blockNumber ? <Link to={`/block/${tx.blockNumber}`} className="table-link">#{tx.blockNumber}</Link> : "pending"}</Row>
            <Row label="Transaction" testId="receipt-hash">{short(tx.hash)}</Row>
            <div className="flex flex-wrap items-center justify-between gap-3 pt-6">
              <ShareBar isTrade={Boolean(tx.trade)} />
              <Link data-testid="receipt-explorer-link" to={`/tx/${tx.hash}`} className="inline-flex items-center gap-1.5 text-sm table-link">View in explorer <ExternalLink size={13} /></Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
